import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Container, Button, Chip,
  Grid, List, ListItem, ListItemText, ListItemIcon, Paper, Divider
} from '@mui/material';
import {
  AccessTime, School, Psychology, FitnessCenter, MusicNote, Palette,
  ArrowBack, PlayArrow, Star, Bookmark, Share
} from '@mui/icons-material';
import SimpleBackButton from '../../components/ui/SimpleBackButton';

const ActivityDetailPage = () => {
  const { pillarSlug, activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [pillarConfig, setPillarConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔍 ActivityDetailPage loaded with:', { pillarSlug, activityId });
    
    const loadActivity = async () => {
      try {
        // Load pillar config
        const configResponse = await fetch('/data/essential-growth-config.json');
        const configData = await configResponse.json();
        const pillar = configData.pillars.find(p => p.slug === pillarSlug);
        
        if (pillar) {
          setPillarConfig(pillar);
        }
        
        // Load activities for this pillar
        const activitiesResponse = await fetch(`/data/essential-growth/${pillarSlug}/activities.json`);
        const activitiesData = await activitiesResponse.json();
        
        // Find activity by slug - search through ALL age groups and ALL categories
        let allActivities = [];
        if (activitiesData.ageGroups && Array.isArray(activitiesData.ageGroups)) {
          activitiesData.ageGroups.forEach(ageGroup => {
            if (ageGroup.categories && Array.isArray(ageGroup.categories)) {
              ageGroup.categories.forEach(category => {
                if (category.activities && Array.isArray(category.activities)) {
                  allActivities = allActivities.concat(category.activities);
                }
              });
            }
          });
        }
        
        console.log(`🔍 Searching through ${allActivities.length} activities for slug: "${activityId}"`);
        
        const foundActivity = allActivities.find(act => {
          if (!act || !act.topic) return false;
          // Create slug using Niche method (simple and consistent)
          const activitySlug = act.topic.toLowerCase().replace(/\s+/g, '-');
          
          const matches = activitySlug === activityId;
          if (matches) {
            console.log(`✅ Found match: "${act.topic}" → slug: "${activitySlug}"`);
          }
          return matches;
        });
        
        if (foundActivity) {
          setActivity(foundActivity);
          console.log('✅ Activity loaded:', foundActivity);
        } else {
          console.error(`❌ Activity not found for slug: "${activityId}" in pillar: "${pillarSlug}"`);
          console.log(`📋 Available activities (first 10):`, allActivities.slice(0, 10).map(a => ({
            topic: a.topic,
            slug: a.topic?.toLowerCase().replace(/\s+/g, '-')
          })));
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading activity:', error);
        setLoading(false);
      }
    };

    loadActivity();
  }, [pillarSlug, activityId]);

  const handleBackClick = () => {
    navigate(`/essential-growth/${pillarSlug}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography >Loading activity...</Typography>
      </Box>
    );
  }

  if (!activity && !loading) {
    // If activity not found after loading, show error with option to go back
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <Typography variant="h5" color="error">Activity not found</Typography>
        <Typography variant="body2" color="text.secondary">
          Activity: {activityId} in pillar: {pillarSlug}
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(`/essential-growth/${pillarSlug}`)}
          startIcon={<ArrowBack />}
        >
          Back to {pillarConfig?.name || pillarSlug}
        </Button>
      </Box>
    );
  }

  const bgGradient = pillarConfig 
    ? `linear-gradient(135deg, ${pillarConfig.primaryColor} 0%, ${pillarConfig.secondaryColor} 100%)`
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: bgGradient,
      padding: '2rem 0'
    }}>
      <Container maxWidth="lg">
        <Card sx={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 4 }}>
            {/* Back Button */}
            <Box mb={3}>
              <SimpleBackButton 
                onClick={handleBackClick}
                size="medium"
              />
            </Box>
            {/* Header */}
            <Box textAlign="center" mb={4}>
              {pillarConfig && (
                <Typography variant="overline" sx={{ 
                  color: pillarConfig.primaryColor,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  {pillarConfig.name}
                </Typography>
              )}
              <Typography variant="h3" sx={{ 
                fontWeight: '900',
                color: '#2d3748',
                mb: 2,
                fontSize: '2.5rem'
              }}>
                {activity.topic}
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#718096',
                mb: 3,
                fontWeight: '400'
              }}>
                {activity.objective}
              </Typography>
              
              <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
                <Chip
                  icon={<AccessTime />}
                  label={activity.estimatedTime}
                  sx={{
                    background: pillarConfig ? `${pillarConfig.primaryColor}20` : 'rgba(34, 197, 94, 0.1)',
                    color: pillarConfig?.primaryColor || '#22c55e',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}
                />
                <Chip
                  label={activity.age}
                  sx={{
                    background: pillarConfig ? `${pillarConfig.primaryColor}15` : 'rgba(59, 130, 246, 0.1)',
                    color: pillarConfig?.primaryColor || '#3b82f6',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}
                />
                {activity.category && (
                  <Chip
                    label={activity.category}
                    sx={{
                      background: pillarConfig ? `${pillarConfig.primaryColor}15` : 'rgba(168, 85, 247, 0.1)',
                      color: pillarConfig?.primaryColor || '#a855f7',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  />
                )}
                {activity.difficultyLevel && (
                  <Chip
                    label={activity.difficultyLevel}
                    sx={{
                      background: 'rgba(251, 146, 60, 0.1)',
                      color: '#fb923c',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Content - Custom Layout */}
            <Box>
              {/* What This Activity Does - Full Width */}
              <Paper sx={{ p: 3, borderRadius: '16px', background: 'rgba(102, 126, 234, 0.05)', mb: 4 }}>
                <Typography  sx={{ fontWeight: 'bold', mb: 2, color: '#2d3748' }}>
                  What This Activity Does
                </Typography>
                <Typography  sx={{ lineHeight: 1.6, color: '#4a5568' }}>
                  {activity.explanation}
                </Typography>
              </Paper>

              {/* Materials and Skills - True Side by Side Horizontally */}
              <Box sx={{ display: 'flex', gap: 3, mb: 4, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                  <Paper sx={{ p: 3, borderRadius: '16px', background: 'rgba(34, 197, 94, 0.05)', height: '100%' }}>
                    <Typography  sx={{ fontWeight: 'bold', mb: 2, color: '#2d3748' }}>
                      Materials Needed
                    </Typography>
                    <List>
                      {activity.activity.materials.map((material, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <Star sx={{ fontSize: 16, color: '#fbbf24' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={material}
                            primaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Paper sx={{ p: 3, borderRadius: '16px', background: 'rgba(255, 193, 7, 0.05)', height: '100%' }}>
                    <Typography  sx={{ fontWeight: 'bold', mb: 2, color: '#2d3748' }}>
                      Skills Your Child Will Develop
                    </Typography>
                    <Box>
                      {activity.activity.skills.map((skill, index) => {
                        // Handle both array and comma-separated string formats
                        const skillText = Array.isArray(skill) ? skill.join('') : skill;
                        // Split by comma if it's a comma-separated string
                        const skillsList = skillText.includes(',') 
                          ? skillText.split(',').map(s => s.trim()).filter(s => s)
                          : [skillText];
                        
                        return skillsList.map((individualSkill, skillIndex) => (
                          <Box key={`${index}-${skillIndex}`} sx={{ mb: 1.5 }}>
                            <Chip
                              label={individualSkill}
                              sx={{
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                                py: 1.5,
                                px: 2,
                                borderRadius: '20px',
                                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)'
                                }
                              }}
                            />
                          </Box>
                        ));
                      })}
                    </Box>
                  </Paper>
                </Box>
              </Box>

              {/* Steps - Full Width */}
              <Paper sx={{ p: 3, borderRadius: '16px', background: 'rgba(168, 85, 247, 0.05)', mb: 4 }}>
                <Typography  sx={{ fontWeight: 'bold', mb: 2, color: '#2d3748' }}>
                  Steps to Follow
                </Typography>
                <List>
                  {activity.activity.steps.map((step, index) => {
                    // Handle both array and comma-separated string formats
                    const stepText = Array.isArray(step) ? step.join('') : step;
                    // Clean up any existing numbering (remove all patterns like "1. ", "2. ", "3. ", etc.)
                    // This handles cases like "3. 3. Step text" -> "Step text"
                    let cleanStep = stepText.trim();
                    // Remove all leading number patterns (handles multiple duplicates)
                    while (cleanStep.match(/^\d+\.\s*/)) {
                      cleanStep = cleanStep.replace(/^\d+\.\s*/, '');
                    }
                    
                    return (
                      <ListItem key={index} sx={{ py: 1.5, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 50, mt: 0.5 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: pillarConfig 
                              ? `linear-gradient(135deg, ${pillarConfig.primaryColor} 0%, ${pillarConfig.secondaryColor} 100%)`
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem'
                          }}>
                            {index + 1}
                          </Box>
                        </ListItemIcon>
                        <ListItemText 
                          primary={cleanStep}
                          primaryTypographyProps={{ 
                            variant: 'body1', 
                            sx: { 
                              lineHeight: 1.6,
                              color: '#2d3748',
                              fontWeight: 500
                            } 
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>

              {/* Tags - Single Line, Small */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography  sx={{ color: '#666', mb: 1 }}>
                  Tags:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
                  {activity.hashtags.map((tag, index) => {
                    // Handle both array and comma-separated string formats
                    const tagText = Array.isArray(tag) ? tag.join('') : tag;
                    // Split by comma if it's a comma-separated string
                    const tagsList = tagText.includes(',') 
                      ? tagText.split(',').map(t => t.trim()).filter(t => t)
                      : [tagText];
                    
                    return tagsList.map((individualTag, tagIndex) => (
                      <Chip
                        key={`${index}-${tagIndex}`}
                        label={individualTag}
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1,
                          borderRadius: '12px',
                          boxShadow: '0 1px 3px rgba(168, 85, 247, 0.3)',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 2px 6px rgba(168, 85, 247, 0.4)'
                          }
                        }}
                      />
                    ));
                  })}
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box textAlign="center" mt={4}>
              <Button
                
                size="large"
                startIcon={<PlayArrow />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '25px',
                  px: 4,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                Start This Activity
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ActivityDetailPage;
